<template>
  <Teleport to="body">
    <Transition
      name="ui-dialog"
      @enter="onEnter"
      @after-enter="onAfterEnter"
      @before-leave="onBeforeLeave"
      @after-leave="onAfterLeave"
    >
      <div
        v-if="visible"
        ref="maskRef"
        class="ui-dialog-mask"
        :class="maskClasses"
        @click="onMaskClick"
        @keydown="onKeyDown"
      >
        <div
          ref="dialogRef"
          class="ui-dialog"
          :class="dialogClasses"
          :style="dialogStyle"
          role="dialog"
          :aria-labelledby="ariaLabelledby"
          :aria-describedby="ariaDescribedby"
          :aria-modal="modal"
          tabindex="-1"
        >
          <!-- Header -->
          <div v-if="showHeader || $slots.header" class="ui-dialog-header" :class="headerClass">
            <slot name="header">
              <span v-if="header" class="ui-dialog-title">{{ header }}</span>
            </slot>
            
            <!-- Close button -->
            <button
              v-if="closable"
              ref="closeButtonRef"
              type="button"
              class="ui-dialog-close-button"
              :aria-label="closeButtonAriaLabel || 'Close'"
              @click="onCloseClick"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.01186 7.00933L12.27 2.75116C12.341 2.68501 12.398 2.60524 12.4375 2.51661C12.4769 2.42798 12.4982 2.33253 12.4999 2.23529C12.5016 2.13805 12.4838 2.04195 12.4476 1.95197C12.4113 1.86199 12.3574 1.78016 12.2884 1.71116C12.2194 1.64216 12.1376 1.58827 12.0476 1.55202C11.9576 1.51577 11.8615 1.49799 11.7643 1.49967C11.667 1.50134 11.5716 1.52243 11.4829 1.56189C11.3943 1.60135 11.3145 1.65828 11.2484 1.72933L7.00019 5.98749L2.75202 1.72933C2.61817 1.60328 2.44131 1.53423 2.25852 1.53723C2.07572 1.54023 1.90112 1.61506 1.77169 1.74448C1.64227 1.8739 1.56744 2.04851 1.56444 2.2313C1.56143 2.41409 1.63048 2.59095 1.75654 2.72481L6.01469 7.00933L1.75654 11.2938C1.63048 11.4277 1.56143 11.6045 1.56444 11.7873C1.56744 11.9701 1.64227 12.1447 1.77169 12.2741C1.90112 12.4035 2.07572 12.4784 2.25852 12.4814C2.44131 12.4844 2.61817 12.4153 2.75202 12.2893L7.00019 8.03116L11.2484 12.2893C11.3822 12.4153 11.5591 12.4844 11.7419 12.4814C11.9247 12.4784 12.0993 12.4035 12.2287 12.2741C12.3581 12.1447 12.433 11.9701 12.436 11.7873C12.439 11.6045 12.3699 11.4277 12.2439 11.2938L8.01186 7.00933Z" fill="currentColor"/>
              </svg>
            </button>
          </div>

          <!-- Content -->
          <div class="ui-dialog-content" :class="contentClass" :style="contentStyle">
            <slot></slot>
          </div>

          <!-- Footer -->
          <div v-if="showFooter || $slots.footer" class="ui-dialog-footer" :class="footerClass">
            <slot name="footer"></slot>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'

export default {
  name: 'UIDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    modal: {
      type: Boolean,
      default: true
    },
    header: {
      type: String,
      default: null
    },
    closable: {
      type: Boolean,
      default: true
    },
    dismissableMask: {
      type: Boolean,
      default: false
    },
    closeOnEscape: {
      type: Boolean,
      default: true
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    showFooter: {
      type: Boolean,
      default: false
    },
    baseZIndex: {
      type: Number,
      default: 0
    },
    autoZIndex: {
      type: Boolean,
      default: true
    },
    position: {
      type: String,
      default: 'center',
      validator: value => ['center', 'top', 'bottom', 'left', 'right', 'topleft', 'topright', 'bottomleft', 'bottomright'].includes(value)
    },
    maximizable: {
      type: Boolean,
      default: false
    },
    resizable: {
      type: Boolean,
      default: false
    },
    draggable: {
      type: Boolean,
      default: false
    },
    minX: {
      type: Number,
      default: 0
    },
    minY: {
      type: Number,
      default: 0
    },
    keepInViewport: {
      type: Boolean,
      default: true
    },
    contentStyle: {
      type: Object,
      default: null
    },
    contentClass: {
      type: String,
      default: null
    },
    headerStyle: {
      type: Object,
      default: null
    },
    headerClass: {
      type: String,
      default: null
    },
    footerStyle: {
      type: Object,
      default: null
    },
    footerClass: {
      type: String,
      default: null
    },
    breakpoints: {
      type: Object,
      default: null
    },
    ariaLabelledby: {
      type: String,
      default: null
    },
    ariaDescribedby: {
      type: String,
      default: null
    },
    closeButtonAriaLabel: {
      type: String,
      default: null
    }
  },
  emits: ['update:visible', 'show', 'hide', 'after-hide', 'maximize', 'unmaximize', 'dragend'],
  setup(props, { emit }) {
    const maskRef = ref(null)
    const dialogRef = ref(null)
    const closeButtonRef = ref(null)
    const lastActiveElement = ref(null)
    const maximized = ref(false)
    
    // Computed properties
    const maskClasses = computed(() => {
      return [
        'ui-dialog-mask-root',
        {
          'ui-dialog-mask-modal': props.modal,
          'ui-dialog-mask-center': props.position === 'center',
          'ui-dialog-mask-top': props.position === 'top',
          'ui-dialog-mask-bottom': props.position === 'bottom',
          'ui-dialog-mask-left': props.position === 'left',
          'ui-dialog-mask-right': props.position === 'right',
          'ui-dialog-mask-topleft': props.position === 'topleft',
          'ui-dialog-mask-topright': props.position === 'topright',
          'ui-dialog-mask-bottomleft': props.position === 'bottomleft',
          'ui-dialog-mask-bottomright': props.position === 'bottomright'
        }
      ]
    })

    const dialogClasses = computed(() => {
      return [
        'ui-dialog-root',
        {
          'ui-dialog-maximized': maximized.value,
          'ui-dialog-resizable': props.resizable,
          'ui-dialog-draggable': props.draggable
        }
      ]
    })

    const dialogStyle = computed(() => {
      const style = {}
      
      if (props.autoZIndex) {
        style.zIndex = props.baseZIndex + 1000 // Add a high z-index for modals
      }
      
      return style
    })

    // Helper functions
    const focus = () => {
      if (dialogRef.value) {
        const focusTarget = dialogRef.value.querySelector('[autofocus]') || 
                           (props.closable ? closeButtonRef.value : null) ||
                           dialogRef.value.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
        
        if (focusTarget) {
          focusTarget.focus()
        } else {
          dialogRef.value.focus()
        }
      }
    }

    const bindGlobalEvents = () => {
      if (props.closeOnEscape) {
        document.addEventListener('keydown', onKeyDown)
      }
      document.addEventListener('focusin', onFocusIn)
    }

    const unbindGlobalEvents = () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('focusin', onFocusIn)
    }

    const onFocusIn = (event) => {
      if (props.modal && dialogRef.value && !dialogRef.value.contains(event.target)) {
        focus()
      }
    }

    // Event handlers
    const onMaskClick = (event) => {
      if (props.dismissableMask && props.closable && maskRef.value === event.target) {
        close()
      }
    }

    const onCloseClick = () => {
      close()
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape' && props.closeOnEscape && props.closable) {
        close()
      }
    }

    const close = () => {
      emit('update:visible', false)
    }

    // Transition handlers
    const onEnter = () => {
      lastActiveElement.value = document.activeElement
      emit('show')
      
      if (props.autoZIndex && maskRef.value) {
        maskRef.value.style.zIndex = String(props.baseZIndex + 1000)
      }
      
      bindGlobalEvents()
    }

    const onAfterEnter = () => {
      focus()
    }

    const onBeforeLeave = () => {
      emit('hide')
    }

    const onAfterLeave = () => {
      unbindGlobalEvents()
      
      if (lastActiveElement.value) {
        lastActiveElement.value.focus()
        lastActiveElement.value = null
      }
      
      emit('after-hide')
    }

    // Watchers
    watch(() => props.visible, (newValue) => {
      if (newValue) {
        nextTick(() => {
          if (maskRef.value) {
            maskRef.value.focus()
          }
        })
      }
    })

    // Cleanup on unmount
    onUnmounted(() => {
      unbindGlobalEvents()
    })

    return {
      maskRef,
      dialogRef,
      closeButtonRef,
      maximized,
      maskClasses,
      dialogClasses,
      dialogStyle,
      onMaskClick,
      onCloseClick,
      onKeyDown,
      onEnter,
      onAfterEnter,
      onBeforeLeave,
      onAfterLeave,
      close
    }
  }
}
</script>

<style scoped>
/* Dialog Mask */
.ui-dialog-mask-root {
  @apply fixed inset-0 flex items-center justify-center;
}

.ui-dialog-mask-modal {
  @apply bg-black bg-opacity-50;
}

/* Position variants */
.ui-dialog-mask-center {
  @apply items-center justify-center;
}

.ui-dialog-mask-top {
  @apply items-start justify-center pt-16;
}

.ui-dialog-mask-bottom {
  @apply items-end justify-center pb-16;
}

.ui-dialog-mask-left {
  @apply items-center justify-start pl-16;
}

.ui-dialog-mask-right {
  @apply items-center justify-end pr-16;
}

.ui-dialog-mask-topleft {
  @apply items-start justify-start p-16;
}

.ui-dialog-mask-topright {
  @apply items-start justify-end p-16;
}

.ui-dialog-mask-bottomleft {
  @apply items-end justify-start p-16;
}

.ui-dialog-mask-bottomright {
  @apply items-end justify-end p-16;
}

/* Dialog */
.ui-dialog-root {
  @apply bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-screen overflow-hidden flex flex-col;
}

.ui-dialog-maximized {
  @apply max-w-none w-full h-full max-h-none mx-0 rounded-none;
}

/* Header */
.ui-dialog-header {
  @apply flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50 rounded-t-lg;
}

.ui-dialog-title {
  @apply text-lg font-semibold text-gray-900;
}

.ui-dialog-close-button {
  @apply p-2 -mr-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500;
}

/* Content */
.ui-dialog-content {
  @apply p-6 flex-1 overflow-y-auto;
}

/* Footer */
.ui-dialog-footer {
  @apply p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-end space-x-3;
}

/* Transitions */
.ui-dialog-enter-active,
.ui-dialog-leave-active {
  transition: all 0.3s ease;
}

.ui-dialog-enter-active .ui-dialog-root,
.ui-dialog-leave-active .ui-dialog-root {
  transition: all 0.3s ease;
}

.ui-dialog-enter-from,
.ui-dialog-leave-to {
  @apply opacity-0;
}

.ui-dialog-enter-from .ui-dialog-root,
.ui-dialog-leave-to .ui-dialog-root {
  @apply transform scale-95 translate-y-4;
}

.ui-dialog-enter-to,
.ui-dialog-leave-from {
  @apply opacity-100;
}

.ui-dialog-enter-to .ui-dialog-root,
.ui-dialog-leave-from .ui-dialog-root {
  @apply transform scale-100 translate-y-0;
}

/* Responsive */
@media (max-width: 640px) {
  .ui-dialog-root {
    @apply max-w-none w-full mx-2 max-h-[calc(100vh-2rem)];
  }
  
  .ui-dialog-header,
  .ui-dialog-content,
  .ui-dialog-footer {
    @apply p-4;
  }
}

/* Focus styles for accessibility */
.ui-dialog-mask-root:focus {
  @apply outline-none;
}

.ui-dialog-root:focus {
  @apply outline-none ring-2 ring-blue-500 ring-inset;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .ui-dialog-root {
    @apply border-2 border-gray-800;
  }
  
  .ui-dialog-header,
  .ui-dialog-footer {
    @apply border-gray-800;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .ui-dialog-enter-active,
  .ui-dialog-leave-active,
  .ui-dialog-enter-active .ui-dialog-root,
  .ui-dialog-leave-active .ui-dialog-root {
    transition: none;
  }
  
  .ui-dialog-close-button {
    transition: none;
  }
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .ui-dialog-root {
    @apply bg-gray-800 text-white;
  }
  
  .ui-dialog-header,
  .ui-dialog-footer {
    @apply bg-gray-700 border-gray-600;
  }
  
  .ui-dialog-title {
    @apply text-white;
  }
  
  .ui-dialog-close-button {
    @apply text-gray-300 hover:text-white hover:bg-gray-600;
  }
}
</style> 